import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  IconButton
} from '@mui/material';
import { Star, Whatshot, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { fetchTopHypeRatedMovieIds } from '../../utils/hypeRatingClient';
import AdSenseAd from '../Ads/AdSenseAd';

const API_BASE_URL = "https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api";

const RecommendationsCarousel = ({ 
  currentMovieId, 
  userId, 
  isAuthenticated, 
  genres, 
  language,
  onMovieClick 
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollRef = useRef();

  useEffect(() => {
    fetchRecommendations();
  }, [currentMovieId, userId, isAuthenticated]);

  const checkScrollPosition = () => {
    const container = scrollRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scroll = (direction) => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScrollPosition, 300);
    }
  };

  useEffect(() => {
    checkScrollPosition();
  }, [recommendations]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      let movieIds;
      
      if (isAuthenticated && userId) {
        // Personalized recommendations for authenticated users
        movieIds = await fetchPersonalizedMovieIds();
      } else {
        // Trending movies for anonymous users - get all movies with HypeRating > 0
        movieIds = await fetchTopHypeRatedMovieIds(currentMovieId, 30, isAuthenticated);
      }
      
      if (movieIds.length === 0) {
        setRecommendations([]);
        return;
      }
      
      // Step 2: Get full movie data from MongoDB using the IDs
      const movies = await fetchMoviesFromMongoDB(movieIds);
      
      // Filter and slice on frontend
      let filteredMovies = movies.filter(movie => 
        movie && movie.id && movie.id !== currentMovieId
      );
      
      // Apply genre/language filtering for personalized recommendations
      if (isAuthenticated && userId && (genres || language)) {
        const genreFiltered = filteredMovies.filter(movie => {
          if (genres && movie.genre) {
            const movieGenres = movie.genre.toLowerCase().split(',').map(g => g.trim());
            const preferredGenres = genres.toLowerCase().split(',').map(g => g.trim());
            if (movieGenres.some(g => preferredGenres.includes(g))) return true;
          }
          if (language && movie.language && movie.language.toLowerCase() === language.toLowerCase()) {
            return true;
          }
          return false;
        });
        
        // Use filtered results if we have enough, otherwise fallback to all
        if (genreFiltered.length >= 10) {
          filteredMovies = genreFiltered;
        }
      }
      
      setRecommendations(filteredMovies.slice(0, 20));
      
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonalizedMovieIds = async () => {
    // For personalized recommendations, just get more movies and filter on frontend
    const allMovieIds = await fetchTopHypeRatedMovieIds(currentMovieId, 50, isAuthenticated);
    return allMovieIds;
  };

  const fetchMoviesFromMongoDB = async (movieIdsWithHype) => {
    try {
      const ids = movieIdsWithHype.map(item => item.id);
      
      // Use GET request with query params instead of POST
      const idsParam = ids.join(',');
      const response = await fetch(`${API_BASE_URL}/movies?ids=${encodeURIComponent(idsParam)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const movies = data.movies || [];
      
      // Merge MongoDB data with HypeRating from DynamoDB
      return movies.map(movie => {
        const hypeData = movieIdsWithHype.find(item => item.id === movie.id);
        return {
          ...movie,
          hypeRating: hypeData?.hypeRating || 0
        };
      });
    } catch (error) {
      console.error('Error fetching movies from MongoDB:', error);
      return [];
    }
  };

  const handleMovieClick = (movie) => {
    if (onMovieClick) {
      onMovieClick(movie);
    }
  };

  const getHypeColor = (rating) => {
    if (rating >= 8) return '#ff4444';
    if (rating >= 6) return '#ff8800';
    if (rating >= 4) return '#ffaa00';
    if (rating >= 2) return '#44aa44';
    return '#888888';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (recommendations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
        No recommendations available
      </Typography>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {recommendations.length > 0 && canScrollLeft && (
        <IconButton
          onClick={() => scroll('left')}
          sx={{
            position: 'absolute',
            left: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            bgcolor: 'background.paper',
            boxShadow: 2,
            display: { xs: recommendations.length > 1 ? 'flex' : 'none', sm: recommendations.length > 3 ? 'flex' : 'none', md: recommendations.length > 4 ? 'flex' : 'none' },
            '&:hover': { bgcolor: 'background.paper' }
          }}
        >
          <ChevronLeft />
        </IconButton>
      )}

      <Box
        ref={scrollRef}
        onScroll={checkScrollPosition}
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          pb: 1
        }}
      >
        {recommendations.map((movie, index) => {
          const hypeRating = movie.hypeRating || 0;
          const hypeColor = getHypeColor(hypeRating);
          const showAd = (index + 1) % 5 === 0;
          
          return (
            <React.Fragment key={movie.id}>
              <Card
                onClick={() => handleMovieClick(movie)}
                sx={{
                  minWidth: 160,
                  maxWidth: 160,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'scale(1.05)' }
                }}
              >
                <CardMedia
                  component="img"
                  height="240"
                  image={movie.poster || "/placeholder-movie.svg"}
                  alt={movie.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ p: 1.5 }}>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      minHeight: '2em',
                      mb: 0.5,
                      fontSize: '0.75rem'
                    }}
                  >
                    {movie.name}
                  </Typography>
                  
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Whatshot sx={{ fontSize: 12, color: hypeColor }} />
                    <Typography variant="caption" sx={{ color: hypeColor, fontWeight: 'bold', fontSize: '0.7rem' }}>
                      {hypeRating.toFixed(1)}
                    </Typography>
                  </Box>

                  {movie.rating && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Star sx={{ fontSize: 12, color: 'gold' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {movie.rating}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
              
              {showAd && (
                <Box sx={{ 
                  minWidth: 160, 
                  maxWidth: 160, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <AdSenseAd 
                    adSlot={`recommendations-carousel-${Math.floor(index / 5)}`}
                    adFormat="vertical"
                    style={{ width: '150px', height: '240px' }}
                  />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {recommendations.length > 0 && canScrollRight && (
        <IconButton
          onClick={() => scroll('right')}
          sx={{
            position: 'absolute',
            right: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            bgcolor: 'background.paper',
            boxShadow: 2,
            display: { xs: recommendations.length > 1 ? 'flex' : 'none', sm: recommendations.length > 3 ? 'flex' : 'none', md: recommendations.length > 4 ? 'flex' : 'none' },
            '&:hover': { bgcolor: 'background.paper' }
          }}
        >
          <ChevronRight />
        </IconButton>
      )}
    </Box>
  );
};

export default RecommendationsCarousel;